import { Icon, Input, Text, theme } from "@findemdev/maui";
import { useMemo, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { ActionsSection, Container, MyCompanyListTitle, SelectCompWithMargin } from "./Filters.styled";
import { ISearchFilter } from "../../../../../types/shared";
import { CompanyBuilder } from "../../../../../constants/namingConventions";
import {
  CompanyListOwnerFilterValue,
  OWNER_FILTER_LABELS,
  QUERY_PARAMS,
  IMPERSONATION_LABELS,
} from "../../../common/constants";
import { useFetchAllUsersQuery } from "../../../../../services/payments";
import UserContext from "../../../../../contexts/UserContext";
import { getFirstName } from "../../../utils/string-format";

interface IFilterProps {
  count: number | undefined;
  setSearchFilter: React.Dispatch<React.SetStateAction<ISearchFilter[] | undefined>>;
  ownerFilter: string;
  setOwnerFilter: (value: string) => void;
  selectedUserId?: string;
};

const Filters = ({
  count,
  setSearchFilter,
  ownerFilter,
  setOwnerFilter,
  selectedUserId,
}: IFilterProps) => {
  const currentUser = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const canImpersonate = useMemo(() => {
    return currentUser?.isOracleUser() || currentUser?.isAdminUser();
  }, [currentUser]);

  const { data: allUsers, isFetching: isFetchingAllUsers } = useFetchAllUsersQuery(
    { joid: currentUser?.getUser().uid || '' },
    { skip: !canImpersonate || !currentUser?.getUser().uid }
  );

  const targetUserId = searchParams.get(QUERY_PARAMS.TARGET_USER_ID);
  const currentUserId = currentUser?.getUser().uid;

  const effectiveSelectedUserId = selectedUserId || targetUserId || currentUserId;
  const showClearIcon = !!targetUserId && targetUserId !== currentUserId;

  const userOptions = useMemo(() => {
    if (!allUsers?.users) return [];
    return allUsers.users.map((user: any) => ({
      label: `${user.local?.name || IMPERSONATION_LABELS.UNKNOWN_USER} (${user.local?.email || IMPERSONATION_LABELS.NO_EMAIL})`,
      value: user._id,
    }));
  }, [allUsers]);

  const selectedUser = useMemo(() => {
    if (!effectiveSelectedUserId) return null;

    if (allUsers?.users) {
      const found = allUsers.users.find((user: any) => user._id === effectiveSelectedUserId);
      if (found) return found;
    }

    if (effectiveSelectedUserId === currentUserId && currentUser) {
      const user = currentUser.getUser();
      return {
        _id: user.uid,
        local: {
          name: user.name,
          email: user.email,
        }
      };
    }

    return null;
  }, [effectiveSelectedUserId, allUsers, currentUserId, currentUser]);

  const ownerFilterOptions = useMemo(() => {
    const userName = selectedUser?.local?.name || '';
    const firstName = getFirstName(userName);
    const isCurrentUser = effectiveSelectedUserId === currentUserId;

    const myListsLabel = isCurrentUser
      ? OWNER_FILTER_LABELS.MY_LISTS
      : (firstName ? `${firstName}'s Lists` : OWNER_FILTER_LABELS.MY_LISTS);

    const sharedLabel = isCurrentUser
      ? OWNER_FILTER_LABELS.SHARED_WITH_ME
      : (firstName ? `Shared with ${firstName}` : OWNER_FILTER_LABELS.SHARED_WITH_ME);

    return [
      { label: OWNER_FILTER_LABELS.ALL_LISTS, value: CompanyListOwnerFilterValue.ALL },
      { label: myListsLabel, value: CompanyListOwnerFilterValue.MY },
      { label: sharedLabel, value: CompanyListOwnerFilterValue.SHARED },
    ];
  }, [selectedUser, effectiveSelectedUserId, currentUserId]);

  const handleUserChange = (value: string | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === currentUserId) {
      newParams.delete(QUERY_PARAMS.TARGET_USER_ID);
    } else if (value) {
      newParams.set(QUERY_PARAMS.TARGET_USER_ID, value);
    } else {
      newParams.delete(QUERY_PARAMS.TARGET_USER_ID);
    }
    setSearchParams(newParams);
  };

  return <Container>
    <MyCompanyListTitle>
      <Icon icon="grid_view" $color={theme.colors['grey-500']} $size={20} />
      <Text variant="defaultHeader">{CompanyBuilder.allCreatedLists.header}</Text>
      <Text
        variant="mediumLead"
        options={[
          'muted'
        ]}
      >
        {count ?? '—'}
      </Text>
    </MyCompanyListTitle>
    <ActionsSection>
      <SelectCompWithMargin
        allowClear={false}
        value={ownerFilter}
        onChange={(value) => setOwnerFilter(value as string)}
        options={ownerFilterOptions}
        style={{ width: "160px" }}
      />
      {canImpersonate && (
        <SelectCompWithMargin
          placeholder={IMPERSONATION_LABELS.SWITCH_USER_PLACEHOLDER}
          value={effectiveSelectedUserId || undefined}
          onChange={(value) => handleUserChange(value as string | null)}
          options={userOptions}
          allowClear={showClearIcon}
          loading={isFetchingAllUsers}
          showSearch
          filterOption={(input, option) => {
            if (!option?.label) return false;
            return (option.label as string).toLowerCase().includes(input.toLowerCase());
          }}
          style={{ width: "240px" }}
        />
      )}
      <Input
        allowClear={true}
        onChange={(e) => {
          const value = e.target.value;
          if (!value) {
            setSearchFilter(undefined);
            return;
          }

          setSearchFilter([{
            field: "name",
            operator: "contains",
            value
          }])
        }}
        debounceDelay={500}
        prefix={<Icon icon="search" $size={20} $color={theme.colors['grey-600']} />}
        placeholder={"Search..."}
        style={{
          width: "210px",
          marginRight: theme.dimensions.spacing[1],
        }}
      />
      {/* <TabsContainer>
        {CREDENTIALS_VIEW_TYPE.map((option) =>
          <TabItem
            $selected={activeTab === option.value}
            onClick={handleTabChange}
            data-tab={option.value}
            key={option.value}
          >
            <Icon icon={option.icon} $size={16} $color={theme.colors['grey-500']} />
          </TabItem>
        )}
        </TabsContainer> */}
      <div></div>
    </ActionsSection>
  </Container>
};

export default Filters;